import React from 'react';
import { useQuery } from 'convex/react';
import { api } from '../lib/convex';
import { formatDistanceToNow } from 'date-fns';
import { FileText, Clock, ChevronRight, Loader2 } from 'lucide-react';
import BlogContent from './BlogWriter/BlogContent';
import { Footerdemo } from './ui/footer-section';
import { Button } from "@/components/ui/button";
import { useNavigate, useParams, Link } from 'react-router-dom';

interface PostItemProps {
  post: any;
  onClick: () => void;
}

function PostListItem({ post, onClick }: PostItemProps) {
  return (
    <div
      onClick={onClick}
      className="bg-white rounded-lg shadow-sm border p-4 hover:shadow-md transition-shadow cursor-pointer"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText className="w-5 h-5 text-blue-600" />
          <h3 className="font-medium text-gray-900">{post.title}</h3>
        </div>
        <ChevronRight className="w-5 h-5 text-gray-400" />
      </div>
      <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
        <Clock className="w-4 h-4" />
        <span>{formatDistanceToNow(post.createdAt)} ago</span>
      </div>
    </div>
  );
}

function PostList() {
  const navigate = useNavigate();
  const posts = useQuery(api.posts.getForCurrentUser);

  const handlePostClick = (postId: string) => {
    navigate(`/posts/${postId}`);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <Link to="/writer">
          <Button
            variant="outline"
            className="text-blue-800 hover:text-blue-900"
          >
            Back to Writer
          </Button>
        </Link>
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Posts</h2>
      <div className="space-y-4">
        {posts?.map((post) => (
          <PostListItem
            key={post._id}
            post={post}
            onClick={() => handlePostClick(post._id)}
          />
        ))}
        
        {(!posts || posts.length === 0) && (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-2">No posts yet</p>
            <p className="text-sm text-gray-400">
              Create your first post in the Content Writer
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function PostViewer() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const selectedPost = useQuery(api.posts.getPostById, 
    slug ? { postId: slug as any } : "skip"
  );

  if (!selectedPost) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          <p className="text-gray-500">Loading post...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <BlogContent
        content={selectedPost.content}
        title={selectedPost.title}
        onReset={() => navigate('/posts')}
      />
    </div>
  );
}

export default function Posts() {
  return (
    <div className="pt-16 min-h-screen flex flex-col">
      <div className="flex-1">{useParams().slug ? <PostViewer /> : <PostList />}</div>
      <Footerdemo />
    </div>
  );
}